/**
 * The two panels that end a level: `FailPresenter` and `SuccessPresenter`.
 *
 * ## Why they exist here at all
 *
 * They are not part of `Gameplay.unity` — `PopupService` instantiates them at
 * runtime under `Bootstrapper.uiRoot` — but they are what the player sees when a
 * level ends, so a port of the in-level UI without them stops halfway through
 * the fail sequence:
 *
 *     GameplayController.OnLevelFailed:                   // __0x211d880.c
 *         if (e.Reason == FailReason.Restart) return;
 *         SoundService.Play(LevelFail);
 *         SplineController.PlayFailCrashAnimation(cb);    // sets crashDone
 *         textAnimator.SetActive(true); Initialize(...); Play();   // sets textDone
 *     <OnLevelFailed>g__TryShowFailPopup:                 // __0x211e610.c
 *         if (!textDone || !crashDone) return;
 *         PopupService.Load(new PopupLoadRequest(...));   // -> FailPresenter
 *
 *     GameplayController.HandleLevelComplete:             // __0x211d6d0.c
 *         EventBus.Publish(new TimeScaleChangedEvent(1f));
 *         BusController.ToggleInput(false);
 *         PopupService.Load(...);                         // -> SuccessPresenter
 *
 * So the loss shows the banner **and then** a panel, and the win goes straight to
 * a panel with no in-scene banner at all.
 *
 * ## What is recoverable, and what is not
 *
 * `Resources/popups/*.prefab` kept every `RectTransform` — 28 for the fail panel,
 * 40 for the win one — so every offset below is the prefab's own number, not a
 * layout invented here. What the prefabs lost is what every other stripped
 * `MonoBehaviour` lost: `Image.m_Sprite`, `m_Color`, and every string.
 *
 * ### Sprites — matched against the shipped **sprite set**, not against node names
 *
 * ⚠️ The first port matched sprites by *node name* (`Popup`, `Header`,
 * `btn_1_green`) and got nearly every one wrong: `Popup.png` is a bare texture
 * with **no `Sprite` asset at all** (`Sprite/Popup.asset` does not exist), and
 * `Header.png` is the yellow ticket the store uses. Two screenshots of the shipped
 * panels (📐, 2026-09-03) settle the table below — every entry is a sprite that
 * exists in `Assets/Sprite/*.asset`, and `popupLayout.test.ts` checks that:
 *
 *     FailPresenter node          sprite                       9-slice (px @ PPU)
 *     ---------------------------------------------------------------------------
 *     BackGroundImage             (flat colour — lost)         —
 *     Vignate                     Vignate                      125 @ 100
 *     Content                     Panel_Yellow(Soft)           125 @ 130
 *     Title/BackgroundImage       Panel_Title_Blue             x 200, y 0 @ 250
 *     CloseButtonBackground       btn_2_red                    simple
 *     CloseButton/icon            Exit                         simple
 *     ContentBackground           Panel_Inside_Yellow(Soft)    125 @ 130
 *     Heart_left / Heart_right    Heart_left / Heart_right     simple
 *     Buttons/ButtonBase          btn_1_base2                  94 @ 300
 *     TryAgainButton              btn_1_orange                 81 @ 300
 *
 * A sliced `Image` draws its border at `border_px * (100 / pixelsPerUnit)` canvas
 * units — the `Sprite.m_PixelsToUnits` column above, read from the (unstripped,
 * u!213) sprite assets — which is where the `border-image-width` values in
 * `index.html` come from.
 *
 * ### Strings — 📐 from the same two screenshots
 *
 * `Level {n}`, `Out of space!`, `Try Again`, `Hold To See The Game` are read off
 * the shipped panels. Title case, not upper case. What is still ❓:
 *
 *  - `SetFailReason` (🔍 `__0x213ac40.c`) picks one of **three** literals —
 *    `reason == 1` (Space), `reason == 2` (OutOfMoves), anything else — and only
 *    the Space one has been seen. The port's single loss is the
 *    `CheckFailCondition` ladder, which is the loss the screenshot shows, so it
 *    takes the Space string.
 *  - `amount_txt` is `m_IsActive: 0` and stays unbuilt.
 *
 * ## The animations
 *
 * 🔍 `BasePopupAnimations$$ShowAnimation__0x20d3740.c` / `$$HideAnimation__0x20d39ec.c`:
 *
 *     Show: background.DOFade(1, 0.2f)  join  animationTransform.DOScale(1, 0.3f)
 *     Hide: background.DOFade(0, 0.1f)  join  animationTransform.DOScale(0, 0.3f)
 *
 * and `FailAnimations` overrides `Show` with its own three-part join (🔍
 * `$$Show__0x2139f24.c`):
 *
 *     AppendInterval(delay)
 *       .Append(backgroundCanvasGroup.DOFade(1, 0.2f))
 *       .Join(frameCanvasGroup.DOFade(1, 0.3f))
 *       .Join(frame.DOScale(1, 0.4f))
 *
 * ❓ Every `SetEase` argument, as everywhere else in this port.
 *
 * ## `HoldButton` — hold to see the board
 *
 * 🔍 `HoldButton$$OnPointerDown__0x2145420.c` / `$$OnPointerUp__0x21456a0.c`:
 *
 *     down: canvasGroup.interactable = false; DOTween.To(alpha, 0, 0.25f)
 *     up:   canvasGroup.interactable = true;  DOTween.To(alpha, 1, 0.25f)
 *
 * 📐 Which `CanvasGroup`: the prefab has three — on `Frame` and `BackGroundImage`,
 * both claimed by `FailAnimations` (`frameCanvasGroup` / `backgroundCanvasGroup`),
 * and one on the **root** that nothing else references. So a hold fades the
 * whole popup, board-cover and all, and the panel comes back on release.
 *
 * ## Deliberately not built
 *
 *  - `ContinueWithRW` — `m_IsActive: 0` in the prefab, and rewarded video is out
 *    of scope.
 *  - `CompleteParticle_L/R`, `Cloud_L/R` — `ParticleImage`s with no settings left.
 *
 * `SuccessPresenter`'s own table, timeline and feature arithmetic are with the
 * class below and in `successFeature.ts`.
 */
import { TEXT_ANIMATOR } from './animatedText'
import {
  SUCCESS_ANIM,
  featureBarCaptions,
  featureUnlockData,
  featureUnlockProgress,
  successUserLevel,
} from './successFeature'

/** 🔍 `BasePopupAnimations`. */
export const POPUP_ANIM = {
  showFadeSeconds: 0.2,
  showScaleSeconds: 0.3,
  hideFadeSeconds: 0.1,
  hideScaleSeconds: 0.3,
  /** 🔍 `FailAnimations.Show` — the frame's own fade and scale run longer. */
  failFrameFadeSeconds: 0.3,
  failFrameScaleSeconds: 0.4,
  /** 🔍 `HoldButton.OnPointerDown/Up` — `DOTween.To(…, 0.25f)` both ways. */
  holdFadeSeconds: 0.25,
}

/**
 * The captions on both panels.
 *
 * 📐 `failReasonSpace`, `tryAgain`, `holdToSeeBoard` are read off a screenshot of
 * the shipped fail panel; `levelComplete` and `continue` off the win one. The
 * remaining two `SetFailReason` literals are ❓ PLACEHOLDER — recorded in
 * `GAMEPLAY-CHECKLIST.md` §10b.
 */
export const POPUP_TEXT = {
  /** `SetFailReason(1)` — `FailReason.Space`. 📐 */
  failReasonSpace: 'Out of space!',
  /** ❓ PLACEHOLDER `SetFailReason(2)` — `FailReason.OutOfMoves`. Never seen. */
  failReasonOutOfMoves: 'Out of moves!',
  /** ❓ PLACEHOLDER — the `else` branch (`Time`, `GiveUp`). Never seen. */
  failReasonOther: 'Level failed!',
  /** `TryAgainButton/txt`. 📐 */
  tryAgain: 'Try Again',
  /** `HoldToSeeBoardHandler/Hold_txt`. 📐 */
  holdToSeeBoard: 'Hold To See The Game',
  /** `Header/LevelCompleteText`. 📐 */
  levelComplete: 'LEVEL COMPLETED',
  /** `FeaturePanel/NewFeatureText`. 📐 */
  newFeature: 'NEW FEATURE',
  /** ❓ PLACEHOLDER — `<Show>b__3` writes a literal over a full bar. Never seen. */
  featureUnlocked: 'UNLOCKED!',
  /** `Continue/ContinueButton/ContinueText`. 📐 */
  continue: 'Continue',
}

export interface PopupHandlers {
  /** `FailModel.TryAgainButtonClick` — ButtonClick, then the scene reloads. */
  onTryAgain(): void
  /**
   * 🔍 `FailPresenter.OnCloseButtonClicked` — the same body as
   * `OnTryAgainButtonClicked`: both flow into `FailModel.TryAgainButtonClick`.
   * `FailModel.CloseButtonClick` is the empty `ret` right after it and has no
   * caller. The cross retries; it never leaves the lost board on screen.
   */
  onCloseFail(): void
  /** `SuccessModel`'s continue path — ButtonClick, then the next level. */
  onContinue(): void
}

/** Build an element with a class and optional children, to keep the tree flat. */
export function el(tag: string, className: string, ...children: (Node | string)[]): HTMLElement {
  const node = document.createElement(tag)
  node.className = className
  node.append(...children)
  return node
}

/** An `<img>` with its sprite already on it — never wired up by index afterwards. */
export function sprite(className: string, file: string): HTMLImageElement {
  const img = document.createElement('img')
  img.className = className
  img.src = `assets/ui/${file}`
  img.alt = ''
  return img
}

/**
 * ⚠️ **A closed panel is hidden with `visibility`, never with `display: none`.**
 *
 * The first version used the `hidden` attribute, which is `display: none`, and a
 * browser does not load the background images of a `display: none` subtree — it
 * waits until the box is generated. So the first loss of a session opened the
 * panel and *then* started three downloads, and the frame, the banner and the
 * button arrived a beat late: the panel's text and its `<img>` children were on
 * screen over the bare board. `<img>` never had the problem, because `src` loads
 * whatever the display is — which is exactly why the broken heart and the close
 * cross were there and the three backgrounds were not.
 *
 * Preloading them with `new Image()` was the first fix and it was the wrong
 * shape: it only made the race shorter. A cold cache still lost it.
 * `visibility: hidden` generates the box, so the backgrounds load with the rest
 * of the page and there is no race left to lose. It costs one absolutely
 * positioned subtree in layout, and takes no clicks — `visibility: hidden` is
 * not hit-tested.
 *
 * `uiAssets.test.ts` holds the line: no popup rule may hide its root with
 * `display: none`.
 */

export abstract class Popup {
  readonly root: HTMLElement
  protected readonly scrim: HTMLElement
  protected readonly frame: HTMLElement
  private closing = 0

  constructor(parent: HTMLElement, id: string) {
    this.scrim = el('div', 'popupScrim')
    this.frame = el('div', 'popupFrame')
    this.root = el('div', 'popup closed', this.scrim, this.frame)
    this.root.id = id
    parent.append(this.root)
  }

  get visible(): boolean {
    return !this.root.classList.contains('closed')
  }

  /** `BasePopupPresenter.Open` → `_animations.Show()`. */
  open(): void {
    window.clearTimeout(this.closing)
    this.root.classList.remove('closed', 'closing')
    // One frame at the closed pose so the transition has somewhere to start.
    requestAnimationFrame(() => this.root.classList.add('open'))
  }

  /** `BasePopupPresenter.Close` → `_animations.Hide()`, then `Unload`. */
  close(): void {
    if (!this.visible) return
    this.root.classList.remove('open')
    this.root.classList.add('closing')
    this.closing = window.setTimeout(() => {
      this.root.classList.add('closed')
      this.root.classList.remove('closing')
    }, POPUP_ANIM.hideScaleSeconds * 1000)
  }
}

/**
 * `FailPresenter`. Geometry, in CanvasScaler units, straight from the prefab:
 *
 *     Content              centred, 538.8 x 629.2958, y +4.621195
 *     Title                stretch-x at the top, height 107.1, y -4.100067
 *       BackgroundImage    centred, 365.5 x 90, y +9
 *       CloseButtonBg      top-right, 74.7961 square, (-39.1, -79.2)
 *         icon             stretch, sizeDelta -38.1909, y +2.3
 *     ContentBackground    stretch, sizeDelta (-50, -252.73853), y +50.3692
 *       HeartBase          centred, 455.57 x 243.879, y +47.682
 *         Heart_left       220.1789 square, (-56.4, -126) from its top
 *         Heart_right      220.1789 square, (54.4, -124.3)
 *       Desc_txt           bottom-centred, 420.76 x 90.1398, y +69.670135
 *     Buttons              bottom-centred, 255.2 x 111.7, y +103.41815
 *       ButtonBase         same rect
 *         TryAgainButton   stretch
 *           txt            195.9 x 105.1855, y +3.4072
 *     HoldToSeeBoard       stretch-x, pivot bottom, height 668.7, y -156
 *       Hold_txt           y +392.72147, height 149.4425
 *
 * A `Title` whose rect starts 49.45 units *above* `Content`'s top edge is not a
 * mistake: anchored to the top with a centred pivot and `y = -4.1`, half of its
 * 107.1 height hangs over the frame. That is the banner overlapping the panel.
 *
 * ❓ `ButtonBase` and `TryAgainButton` share one rect in the prefab, yet the
 * shipped panel shows `btn_1_base2`'s gold rim around the orange button. The
 * inset that exposes it is not in the prefab (it would live on the stripped
 * `Image`/`Button`); `--buttonInset` in `index.html` is measured off the
 * screenshot and marked PLACEHOLDER there.
 */
export class FailPopup extends Popup {
  private readonly levelText: HTMLElement
  private readonly reasonText: HTMLElement

  constructor(parent: HTMLElement, handlers: PopupHandlers) {
    super(parent, 'failPopup')

    this.levelText = el('span', 'popupLevelText')
    this.reasonText = el('span', 'popupDesc')
    this.reasonText.textContent = POPUP_TEXT.failReasonSpace

    const close = el('button', 'popupClose', sprite('popupCloseIcon', 'Exit.png')) as HTMLButtonElement
    close.type = 'button'
    close.setAttribute('aria-label', 'close')
    close.addEventListener('click', () => {
      this.close()
      handlers.onCloseFail()
    })

    const tryAgain = el('button', 'popupButton') as HTMLButtonElement
    tryAgain.type = 'button'
    tryAgain.append(el('span', 'popupButtonText', POPUP_TEXT.tryAgain))
    tryAgain.addEventListener('click', () => handlers.onTryAgain())

    this.frame.append(
      el('div', 'failTitle', el('div', 'failTitleBg', this.levelText), close),
      el(
        'div',
        'failBody',
        el(
          'div',
          'heartBase',
          sprite('heartLeft', 'Heart_left.png'),
          sprite('heartRight', 'Heart_right.png'),
        ),
        this.reasonText,
      ),
      el('div', 'failButtons', el('div', 'popupButtonBase', tryAgain)),
    )

    // `HoldToSeeBoardHandler` — an `Image` that takes the press, so the band
    // itself is the hit area, not just the caption.
    const hold = el('div', 'holdToSee', el('span', 'holdText', POPUP_TEXT.holdToSeeBoard))
    const release = () => this.root.classList.remove('held')
    hold.addEventListener('pointerdown', (e) => {
      e.preventDefault()
      this.root.classList.add('held')
    })
    hold.addEventListener('pointerup', release)
    hold.addEventListener('pointercancel', release)
    hold.addEventListener('pointerleave', release)
    // The prefab's sibling order is `BackGroundImage`, `HoldToSeeBoardHandler`,
    // `Overlay` — the band is *under* the frame, so its 668.7-unit hit area
    // (which reaches up behind the Try Again button) never takes the frame's
    // clicks. Appending it last put it on top and ate them.
    this.root.insertBefore(hold, this.frame)
  }

  /** `FailPresenter.levelText`, and `SetFailReason` writing `failTypeText`. */
  setLevel(number: number): void {
    this.levelText.textContent = `Level ${number}`
  }

  /** `HoldButton` state — the popup's own `CanvasGroup` is at alpha 0. */
  get held(): boolean {
    return this.root.classList.contains('held')
  }
}

/**
 * `SuccessPresenter`. Geometry from the prefab:
 *
 *     LevelCompleteText        stretch-x at the top, pivot top, y -200, height 216
 *     Content                  stretch-x at the centre, y +73.9, height 100
 *       SuccessFeatureProgress stretch-x, height 213
 *         FeaturePanel         stretch
 *           Glow               stretch, sizeDelta (29.38, 38.06), y +0.49
 *           PanelImage         stretch
 *           NewFeatureText     top-centre, (-78.2, -70.3), 168.5 x 41.2
 *           FeatureIcon        centred, (101.6, 0), 128.27 square
 *           FillImage          centred, (-85.4, -17.7), 194.27 x 43.88
 *             FillText         stretch, y +1
 *       CoinPanel              stretch-x, height 300
 *         FeatureIcon          centred, 211 square
 *     Buttons                  bottom-centred, pivot bottom, 250 x 312.94
 *       Continue               245.04 x 120, centre 206.47 below Buttons' top
 *         ContinueButtonBackground / ContinueButton   stretch
 *     ConfettiParticleImage    stretch-x at the top
 *
 * Sprites (📐 from the screenshot of the shipped panel, every one a `Sprite`
 * asset): `PanelImage` = `NewFeaturesBG`, `FillImage` = `NewFeaturesProgress`,
 * `Glow` = `NewFeatures_Glow`, `FeatureIcon` = the feature's own icon
 * (`Lift` in the screenshot), `ContinueButtonBackground` = `btn_1_base2`,
 * `ContinueButton` = `btn_1_blue`, confetti from the `confetti 1` sheet.
 * ❓ `CoinPanel/FeatureIcon` (the no-feature branch) keeps `coin` — never seen.
 *
 * `PanelImage` stretches to `FeaturePanel` in the prefab, but the screenshot
 * shows `NewFeaturesBG` at its own 486:242 — so it is drawn with its aspect kept,
 * 213 tall, and the children are placed off that box's centre.
 *
 * The title is `MikadoBlack_LevelCompleted.mat` (🔍, the material survived):
 * outline `(1, 0.952, 0.693)` width 0.071, underlay `(0.584, 0.327, 0.208)`
 * offset y -1 dilate 0.398 — a cream rim and a thick brown drop. ❓ The face
 * colour is the TMP vertex colour, lost with the component; gold per the
 * screenshot. It is played in by the same `TextAnimator` as the fail banner,
 * from the overlay fade's `OnComplete` — see `successFeature.ts`.
 *
 * ❓ `ConfettiParticleImage` / `CompleteParticle_L/R` / `Cloud_L/R` are
 * `ParticleImage`s whose settings went with the component. All of them are the
 * nine sprites of the sheet, placed by a fixed hash so two runs agree; count,
 * speed and spread are PLACEHOLDER. `ConfettiParticleImage` falls from the top
 * on the overlay's `OnComplete`; the other four are `completeParticles`, two
 * co-located emitters per side, all fired at t = 0 before the sequence exists
 * — see `completeBurst`.
 */
export class SuccessPopup extends Popup {
  private readonly title: HTMLElement
  private readonly featurePanel: HTMLElement
  private readonly featureIcon: HTMLImageElement
  private readonly fillText: HTMLElement
  private readonly coinPanel: HTMLElement
  /** `_featureSequence` / `_featureSubSequence` — killed with the panel. */
  private timers: number[] = []

  constructor(parent: HTMLElement, handlers: PopupHandlers) {
    super(parent, 'successPopup')

    this.title = el('div', 'successTitle')

    this.featureIcon = sprite('featureIcon', 'Lift.png')
    this.fillText = el('span', 'fillText')
    this.featurePanel = el(
      'div',
      'featurePanel',
      sprite('featureGlow', 'NewFeatures_Glow.png'),
      sprite('featurePanelImage', 'NewFeaturesBG.png'),
      el('span', 'newFeatureText', POPUP_TEXT.newFeature),
      this.featureIcon,
      el('div', 'fillImage', el('div', 'fillBar', sprite('fillSprite', 'NewFeaturesProgress.png')), this.fillText),
    )
    this.coinPanel = el('div', 'coinPanel', sprite('successCoin', 'coin.png'))

    const cont = el('button', 'popupButton') as HTMLButtonElement
    cont.type = 'button'
    cont.append(el('span', 'popupButtonText', POPUP_TEXT.continue))
    cont.addEventListener('click', () => handlers.onContinue())

    this.frame.append(
      this.title,
      el('div', 'successContent', el('div', 'successFeatureProgress', this.featurePanel), this.coinPanel),
      el('div', 'successButtons', el('div', 'popupButtonBase continueButton', cont)),
    )
    // Prefab draw order: `Overlay` (which owns `ConfettiParticleImage`) first,
    // then the four root-level emitters — so the side bursts sit on top.
    // 📐 Four of them, two per side: `CompleteParticle_?_1` + `Cloud_?`.
    this.root.append(confetti())
    for (const side of ['left', 'right'] as const) {
      for (let slot = 0; slot < COMPLETE_BURST_EMITTERS_PER_SIDE; slot++) {
        this.root.append(completeBurst(side, slot))
      }
    }
  }

  /**
   * `SuccessAnimations.Initialize(soundService, userLevel, featureData)` then
   * `Show()`: pick the branch, write the bar, and let the CSS timeline run.
   */
  show(completedLevel: number): void {
    const userLevel = successUserLevel(completedLevel)
    const feature = featureUnlockData(userLevel)
    // `featurePanel.SetActive(has)` / `coinPanel.SetActive(!has)`.
    this.featurePanel.hidden = !feature
    this.coinPanel.hidden = !!feature
    this.root.classList.remove('full')
    for (const t of this.timers) window.clearTimeout(t)
    this.timers = []
    if (feature) {
      const [a, b] = featureUnlockProgress(userLevel)
      const captions = featureBarCaptions(a, b)
      this.featureIcon.src = `assets/ui/${feature.icon}`
      this.fillText.textContent = captions.before
      this.root.style.setProperty('--fill0', String((a - 1) / b))
      this.root.style.setProperty('--fill1', String(a / b))
      // `<Show>b__2` — the fill tween's OnComplete: the caption catches up, and
      // a full bar pops the icon, fades the glow in, then (`b__3`) swaps the
      // caption for the unlock word. `?instant=1` collapses the clock.
      const instant = document.documentElement.classList.contains('instant')
      const at = (seconds: number, fn: () => void) => {
        if (instant) fn()
        else this.timers.push(window.setTimeout(fn, seconds * 1000))
      }
      const landed = SUCCESS_ANIM.fillAt + SUCCESS_ANIM.fillTween
      at(landed, () => {
        this.fillText.textContent = captions.after
        if (!captions.full) return
        this.root.classList.add('full')
        at(SUCCESS_ANIM.fullIconPop * 2, () => {
          this.fillText.textContent = POPUP_TEXT.featureUnlocked
        })
      })
    }

    // `TextAnimator.Initialize(text)` — every character scaled to zero, each
    // inserted at `index * delayBetweenChars`, and the whole run starts when
    // the overlay's fade completes.
    this.title.replaceChildren()
    const start = SUCCESS_ANIM.leadIn + SUCCESS_ANIM.overlayFade
    let index = 0
    for (const word of POPUP_TEXT.levelComplete.split(' ')) {
      const box = el('span', 'word')
      for (const ch of word) {
        const span = document.createElement('span')
        span.textContent = ch
        span.style.animationDelay = `${start + index * TEXT_ANIMATOR.delayBetweenChars}s`
        box.append(span)
        index++
      }
      // The space still takes a slot in TMP's character list.
      index++
      this.title.append(box, ' ')
    }
    this.open()
  }

  override close(): void {
    for (const t of this.timers) window.clearTimeout(t)
    this.timers = []
    super.close()
  }
}

/**
 * ❓ PLACEHOLDER confetti — `ConfettiParticleImage`'s settings are gone.
 *
 * Pieces from the nine-sprite `confetti 1` sheet, each placed by a small LCG
 * on its index (no `Math.random`, so a screenshot is repeatable), falling from
 * the top edge — where the prefab anchors the emitter.
 *
 * ❓ **Count and waves are tuned to the shipped build, not read.** The user
 * watched the original panel and reports the rain is both denser and repeated;
 * the port had one wave of 24 and stopped. Nothing in the kit contradicts that
 * — quite the opposite, it is what a `ParticleImage` does: `Show_b__0` calls
 * `DoPlay()` **once**, but the emitter's `_loop` (0x16C), `_duration` (0x170)
 * and rate are all stripped from the prefab (see the header comment), so one
 * `Play()` can rain for as long as its duration says. A single short burst was
 * the *narrowest* reading of that call, not the only one.
 *
 * To settle it we need the original `ParticleImage` serialization or a clean
 * capture of the won panel; until then this is 📐-free tuning and stays ❓.
 */
export const CONFETTI_COUNT = 30

/** ❓ Waves of `CONFETTI_COUNT`, `CONFETTI_WAVE_GAP` apart — see above. */
export const CONFETTI_WAVES = 3

/** ❓ Seconds between waves. */
export const CONFETTI_WAVE_GAP = 1.1

/**
 * 🔍 `completeParticles` fire at t = 0 — a second before the overlay.
 *
 * `SuccessAnimations$$Show__0x21407a8.c` opens by walking the array at 0x40
 * (`ParticleImage[] completeParticles`, dump.cs:713817), calling
 * `ParticleImage__Play(lVar1, 0)` and `SoundService__Play(…, 1, 0)` on every
 * element (:64-80). That loop runs **before** `DG_Tweening_DOTween__Sequence()`
 * (:88), so it is not in the timeline at all; the timeline then opens with
 * `AppendInterval(0x3f800000)` = 1 s (:97) and only after it does
 * `overlayCanvasGroup` (0x58) `DOFade(1, 0x3e99999a = 0.3)` (:108-113).
 *
 * The port used to build only `ConfettiParticleImage`, which hangs off the
 * overlay's `OnComplete` (`…DisplayClass22_0$$_Show_b__0__0x214144c.c` reads
 * +0x38 and calls `ParticleImage__DoPlay`) — i.e. 1.3 s. So the first 1.3 s of
 * a won level showed nothing whatsoever. These four emitters are the missing
 * t = 0 half.
 *
 * 📐 Where they sit, from `Resources/popups/SuccessPresenter.prefab`. The four
 * emitters — `CompleteParticle_L_1` (:2111), `Cloud_L` (:2151),
 * `CompleteParticle_R_1` (:2191), `Cloud_R` (:2231) — are children of the
 * **root** RectTransform (`m_Father: 224217368724061974`), *not* of `Overlay`
 * (`224808113985169905`). That is what keeps them visible: `Initialize` zeroes
 * the alpha of 0x58 and 0x60 only, so nothing gates them while the panel waits.
 * Their anchored pivot points mirror exactly — left is anchor (0, 0.5) plus
 * (-50, 200), right is anchor (1, 0.5) plus (50, 200) — i.e. 50 u outside each
 * screen edge, 200 u above the centre. Both rects are 100 x 100.
 *
 * ❓ PLACEHOLDER — everything about the particles themselves. All five
 * `ParticleImage`s exported as MonoBehaviour guid
 * `c496def4ab12df00d24f250312307cf6` with an empty `m_Name` and **no fields at
 * all**, so count, speed, spread, gravity, lifetime, colour and sprite are
 * invented here. Two specifics worth naming:
 *
 *  - The 45 degree launch takes its *magnitude* from the rects' rotation
 *    (`m_LocalEulerAnglesHint` z = +44.999996 left, -44.999996 right) but its
 *    *sign* is a mirror we chose. The prefab's two signs do not resolve to one
 *    shared local axis — left's local +x points up-and-inward, right's local +x
 *    points down-and-outward — and `ParticleImage`'s emission convention went
 *    with the component, so the rotation cannot settle which way they fire.
 *  - `Cloud_L` / `Cloud_R` are named for a sprite the port does not ship.
 *    `Sprite/PuffCloud.asset` is the only cloud in the exported sprite set and
 *    is the likely one, but it is not in `UI_SPRITES`, so all four emitters
 *    reuse the `confetti 1` sheet here.
 *
 * To settle any of it we need the original `ParticleImage` serialization or a
 * clean capture of the panel's first second.
 */
export const COMPLETE_BURST_PER_SHOT = 12

/**
 * ❓ Shots per emitter, `COMPLETE_BURST_SHOT_GAP` apart — user-directed.
 *
 * Same reasoning as `CONFETTI_WAVES`: `Show` calls `Play()` once per element,
 * but a `ParticleImage` with `_loop` / `_duration` (both stripped) keeps
 * emitting after that one call, so a single `Play()` is a burst *train*, not a
 * single puff. The user watched the shipped panel and reports exactly that.
 */
export const COMPLETE_BURST_SHOTS = 3

/** ❓ Seconds between shots of one emitter. */
export const COMPLETE_BURST_SHOT_GAP = 0.45

/**
 * 📐 `completeParticles` holds **four** emitters, so `Show`'s loop fires four
 * times, not twice.
 *
 * `SuccessPresenter.prefab` gives five `ParticleImage`s (MonoBehaviour guid
 * `c496def4ab12df00d24f250312307cf6`): `ConfettiParticleImage` under `Overlay`,
 * and `CompleteParticle_L_1` (:2111), `Cloud_L` (:2151), `CompleteParticle_R_1`
 * (:2191), `Cloud_R` (:2231) under the root. The four root ones are the array
 * `Show` walks — and the L pair share one rect to the byte (anchor (0, 0.5),
 * (-50, 200), 100 x 100, pivot (1, 1)), as do the R pair, i.e. each side is
 * **two co-located emitters**, a puff stacked on a confetti throw.
 *
 * The port drew one per side, so half of the t = 0 burst was missing. Locked
 * against the prefab in `tests/popupLayout.test.ts`.
 */
export const COMPLETE_BURST_EMITTERS_PER_SIDE = 2

/** 🔍 Seconds after the panel opens — the `Play()` loop precedes the sequence. */
export const COMPLETE_BURST_AT = 0

/**
 * One emitter. `side` is the prefab's L/R pair; pieces fly up and inward, so
 * the left ones throw right and the right ones throw left. `slot` is which of
 * that side's two co-located emitters this is (`CompleteParticle_?_1` = 0,
 * `Cloud_?` = 1) — same origin, different seed, so the two do not overlap.
 */
function completeBurst(side: 'left' | 'right', slot: number): HTMLElement {
  const box = el('div', `completeBurst ${side}`)
  const inward = side === 'left' ? 1 : -1
  // Same shape of LCG as `confetti()` — no `Math.random`, so a screenshot of
  // the panel is repeatable. Seeded off side and slot so no two agree.
  let seed = ((side === 'left' ? 0x2545f491 : 0x85ebca6b) ^ Math.imul(slot + 1, 0x27d4eb2d)) >>> 0
  const next = () => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0
    return seed / 0x100000000
  }
  for (let i = 0; i < COMPLETE_BURST_PER_SHOT * COMPLETE_BURST_SHOTS; i++) {
    const piece = document.createElement('span')
    const cell = i % 9
    const shot = Math.floor(i / COMPLETE_BURST_PER_SHOT)
    piece.style.backgroundPosition = `${(cell % 3) * 50}% ${Math.floor(cell / 3) * 50}%`
    // A fan about the 45 degree axis: +-22 degrees, i.e. from level-inward to
    // straight up. Speed is what carries a piece across the screen in `--dur`.
    const angle = ((45 + (next() * 44 - 22)) * Math.PI) / 180
    const speed = 900 + next() * 700
    piece.style.setProperty('--dx', (Math.cos(angle) * speed * inward).toFixed(1))
    piece.style.setProperty('--dy', (-Math.sin(angle) * speed).toFixed(1))
    piece.style.setProperty('--fall', (1100 + next() * 500).toFixed(1))
    piece.style.setProperty('--dur', `${(1.5 + next() * 0.9).toFixed(2)}s`)
    piece.style.setProperty('--delay', `${(shot * COMPLETE_BURST_SHOT_GAP + next() * 0.18).toFixed(2)}s`)
    piece.style.setProperty('--spin', `${Math.round(next() * 900 - 450)}deg`)
    box.append(piece)
  }
  return box
}

function confetti(): HTMLElement {
  const box = el('div', 'confetti')
  let seed = 0x9e3779b1
  const next = () => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0
    return seed / 0x100000000
  }
  for (let i = 0; i < CONFETTI_COUNT * CONFETTI_WAVES; i++) {
    const piece = document.createElement('span')
    const cell = i % 9
    const wave = Math.floor(i / CONFETTI_COUNT)
    piece.style.backgroundPosition = `${(cell % 3) * 50}% ${Math.floor(cell / 3) * 50}%`
    piece.style.left = `${(next() * 96 + 2).toFixed(2)}%`
    piece.style.setProperty('--delay', `${(wave * CONFETTI_WAVE_GAP + next() * 1.5).toFixed(2)}s`)
    piece.style.setProperty('--dur', `${(2.8 + next() * 1.8).toFixed(2)}s`)
    piece.style.setProperty('--spin', `${Math.round(next() * 720 - 360)}deg`)
    box.append(piece)
  }
  return box
}
